import { useState } from "react";
import { Hash, ArrowRight, ArrowLeft, Check, Loader2, ShoppingCart, Camera, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useValidateConversion, useConversionPreview } from "@/hooks/useCampaigns";
import { calcularConversao } from "@/lib/conversionCalculator";
import { toast } from "sonner";
import type { Conversion, RemuneracaoModelo } from "@/types/api";
import QrCodeScanner from "@/components/QrCodeScanner";

type Step = "method" | "scan" | "input" | "preview" | "success";

interface PreviewData {
  valorBruto: number;
  descontoValor: number;
  remuneracaoValor: number;
  valorLiquido: number;
  shortToken: string;
}

function fmt(n: number) {
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Sell() {
  const [step, setStep] = useState<Step>("method");
  const [shortToken, setShortToken] = useState("");
  const [valorStr, setValorStr] = useState("");
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [result, setResult] = useState<Conversion | null>(null);
  const validate = useValidateConversion();
  const fetchPreview = useConversionPreview();

  function handlePreview() {
    const token = shortToken.trim();
    const valor = parseFloat(valorStr.replace(",", "."));

    if (!token || token.length < 4) {
      toast.error("Digite o código da campanha.");
      return;
    }
    if (isNaN(valor) || valor <= 0) {
      toast.error("Digite um valor de venda válido.");
      return;
    }

    fetchPreview.mutate(token, {
      onSuccess: (data) => {
        const calc = calcularConversao(
          valor,
          data.remuneracao_modelo as RemuneracaoModelo,
          parseFloat(data.remuneracao_valor),
          data.desconto_pct != null ? parseFloat(data.desconto_pct) : null,
        );
        setPreview({ ...calc, shortToken: token });
        setStep("preview");
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Código inválido ou campanha inativa.";
        toast.error(msg);
      },
    });
  }

  function handleConfirm() {
    const token = shortToken.trim();
    const valor = parseFloat(valorStr.replace(",", "."));

    validate.mutate(
      { qrcode_token: token, valor_bruto: valor },
      {
        onSuccess: (data) => {
          setResult(data);
          setStep("success");
          toast.success("Venda registrada com sucesso!");
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Erro ao registrar venda.";
          toast.error(msg);
        },
      }
    );
  }

  function handleReset() {
    setStep("method");
    setShortToken("");
    setValorStr("");
    setPreview(null);
    setResult(null);
  }

  return (
    <div className="space-y-6 max-w-md mx-auto">
      <h1 className="text-2xl font-bold text-foreground">Registrar Venda</h1>

      {step === "method" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              Registrar Venda
            </CardTitle>
            <CardDescription>
              Como deseja identificar o indicador?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full gap-2 h-14 text-base"
              onClick={() => setStep("scan")}
            >
              <Camera className="h-5 w-5" />
              Escanear QR Code
            </Button>
            <Button
              variant="outline"
              className="w-full gap-2 h-14 text-base"
              onClick={() => setStep("input")}
            >
              <Keyboard className="h-5 w-5" />
              Inserir código manualmente
            </Button>
          </CardContent>
        </Card>
      )}

      {step === "scan" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              Escanear QR Code
            </CardTitle>
            <CardDescription>
              Aponte a câmera para o QR code do indicador.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <QrCodeScanner
              onScan={(code) => {
                setShortToken(code);
                setStep("input");
              }}
              onClose={() => setStep("method")}
            />
          </CardContent>
        </Card>
      )}

      {step === "input" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Hash className="h-5 w-5 text-primary" />
              Código da Campanha
            </CardTitle>
            <CardDescription>
              Peça o código curto ao indicador (ex: XpT250) e informe o valor da venda.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shortToken">Código da campanha</Label>
              <Input
                id="shortToken"
                placeholder="Ex: XpT250"
                value={shortToken}
                onChange={(e) => setShortToken(e.target.value.trim())}
                className="font-mono uppercase tracking-widest"
                maxLength={8}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="valor">Valor da venda (R$)</Label>
              <Input
                id="valor"
                type="number"
                placeholder="0,00"
                min="0.01"
                step="0.01"
                value={valorStr}
                onChange={(e) => setValorStr(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setStep("method")}
                disabled={fetchPreview.isPending}
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handlePreview}
                disabled={fetchPreview.isPending}
              >
                {fetchPreview.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>Continuar <ArrowRight className="h-4 w-4" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "preview" && preview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              Confirmar Venda
            </CardTitle>
            <CardDescription>Verifique os dados antes de registrar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Código</span>
                <span className="font-mono font-bold">{preview.shortToken}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor da venda</span>
                <span className="font-semibold">R$ {fmt(preview.valorBruto)}</span>
              </div>
              {preview.descontoValor > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto cliente</span>
                  <span>- R$ {fmt(preview.descontoValor)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold">
                <span>Valor líquido</span>
                <span>R$ {fmt(preview.valorLiquido)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => setStep("input")}
                disabled={validate.isPending}
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleConfirm}
                disabled={validate.isPending}
              >
                {validate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Confirmar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "success" && result && (
        <Card className="border-green-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              Venda Registrada!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor bruto</span>
                <span className="font-semibold">R$ {fmt(parseFloat(result.valor_bruto))}</span>
              </div>
              {parseFloat(result.desconto_valor) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto cliente</span>
                  <span>- R$ {fmt(parseFloat(result.desconto_valor))}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Valor líquido</span>
                <span>
                  R$ {fmt(parseFloat(result.valor_bruto) - parseFloat(result.desconto_valor))}
                </span>
              </div>
            </div>
            <Button className="w-full" onClick={handleReset}>
              Registrar Nova Venda
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
